import React, { ReactElement, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import casePdfGeneratorStyle from "./casePdfGeneratorStyle";
import { Checkbox, FormControl, FormLabel, ListItemIcon, ListItemText, Paper } from "@mui/material";
import _ from "lodash";
import {
  getDocumentName,
  getExternalReference,
  getExternalReferenceSelectValue,
  getReferenceOptions,
  getSenderMediumValue,
} from "./casePdfGeneratorService";
import SelectElement from "../../ReactHookFormElements/SelectElement";
import { RadioButtonGroup, useFormContext } from "react-hook-form-mui";
import { MediaObject } from "../../../types/MediaObject";
import LegalbirdTipTapEditor from "../../LbEditor/LegalbirdTipTapEditor";
import { saveSessionStorageState } from "../../../services/browserStorageService";
import { UseForm } from "../../../types/UseForm";
import { useCurrentUser } from "../../../provider/CurrentUserProvider";
import { ProductUnion } from "../../../types/ProductUnion";
import { getPdfGeneratorFormElementConfig } from "../../../services/pdfGeneratorFormService";
import dayjs from "dayjs";
import FormElementReactHookForm from "../CaseForm/FormElementReactHookForm";
import { AvailableDocumentRecipient } from "../../../types/AvailableDocumentRecipient";
import { getDocumentRecipientRole } from "../../../services/documentRecipientService";

type PdfGeneratorFormElementsProps = {
  product: ProductUnion;
  mediaObjects: MediaObject[];
  minimalView?: boolean;
  additionalEditorToolbarActions?: ReactElement[];
  availableRecipients: AvailableDocumentRecipient[];
  recipientSelectionDisabled?: boolean;
};

const documentTemplates = {
  customPdf: "Individueller Schriftsatz",
  extrajudicialLetter: "Außergerichtliches Schreiben (inkl. Vollmacht)",
};

const isPasswordProtected = (mediaObject: MediaObject): boolean => mediaObject.metaData?.isEncrypted === true;

const PdfGeneratorFormElements = ({
  product,
  mediaObjects,
  minimalView = false,
  additionalEditorToolbarActions,
  availableRecipients,
  recipientSelectionDisabled = false,
}: PdfGeneratorFormElementsProps) => {
  const currentUser = useCurrentUser();

  const isDocumentTemplateEnabled =
    ([
      "generalCivilLaw",
      "employmentContract",
      "civilServiceLaw",
      "rentalContract",
      "divorce",
      "trafficAccident",
    ].includes(product.productClassName) ||
      product.productCategory === "contractLaw") &&
    !currentUser.isExternal;
  const formContext = useFormContext();
  const selectedMediaObjectIds: number[] = formContext.watch("selectedMediaObjects") ?? [];

  useEffect(() => {
    const unencryptedSelectedMediaObjectIds = selectedMediaObjectIds.filter(
      (selectedMediaObjectId) =>
        !mediaObjects.some(
          (mediaObject) => mediaObject.id === selectedMediaObjectId && isPasswordProtected(mediaObject),
        ),
    );

    if (unencryptedSelectedMediaObjectIds.length !== selectedMediaObjectIds.length) {
      formContext.setValue("selectedMediaObjects", unencryptedSelectedMediaObjectIds);
    }
  }, [mediaObjects, selectedMediaObjectIds]);

  useEffect(() => {
    saveFormValuesBackup(formContext.getValues(), product);
  }, [formContext.watch()]);

  useEffect(() => {
    if (formContext.getValues("sendEmailToCustomer") === "true") {
      formContext.setValue("showInGallery", "true");
    }
  }, [formContext.watch("sendEmailToCustomer")]);

  const renderSelectedMediaObject = (selected: number[]) => {
    //map over the selected array and find the related mediaobject to get its description
    if (!mediaObjects.length) {
      return "";
    }
    return _.map(
      selected,
      (selectedMediaObject) =>
        _.find(mediaObjects, (mediaObject) => mediaObject.id === selectedMediaObject)?.description,
    ).join(", ");
  };

  const referenceOptions = useMemo(
    () => _.filter(getReferenceOptions(product), (option) => !option.isHidden),
    [product],
  );

  const handleExternalReferenceSelectChange = (value: string) => {
    formContext.setValue("externalReferenceSelect", value);
    formContext.setValue("externalReference", getExternalReference(product, value) ?? "");
  };

  const handleRecipientTypeChange = (value: string) => {
    const filename = getDocumentName(value, formContext.getValues("filename") ?? "", formContext.getValues("recipientType"), getDocumentRecipientRole(product, value));
    formContext.setValue("recipientType", value);
    formContext.setValue("filename", filename);
    formContext.setValue("senderMedium", getSenderMediumValue(value));
    handleExternalReferenceSelectChange(getExternalReferenceSelectValue(product, value));

    switch (value) {
      case "opposingCarLiabilityInsurance":
        formContext.setValue("insurancePolicyNumber", product.opposingInsurancePolicyNumber || "");
        formContext.setValue("caseReferenceNumber", product.opposingCaseReferenceNumber || "");
        break;
      case "opposingLiabilityInsurance":
        formContext.setValue("insurancePolicyNumber", product.opposingLiabilityInsurancePolicyNumber || "");
        formContext.setValue("caseReferenceNumber", product.opposingLiabilityInsuranceCaseReferenceNumber || "");
        break;
      default:
        formContext.setValue("insurancePolicyNumber", product.caseInsurance.insurancePolicyNumber || "");
        formContext.setValue("caseReferenceNumber", product.caseInsurance.caseReferenceNumber || "");
        break;
    }
  };

  const handleDocumentTemplateChange = (value: string) => {
    formContext.setValue("documentTemplate", value);

    if (value === "extrajudicialLetter" && referenceOptions.find((option) => option.type === "opposingCounsel")) {
      handleExternalReferenceSelectChange("opposingCounsel");
    } else if (
      value === "extrajudicialLetter" &&
      referenceOptions.find((option) => option.type === "opposingPartyReference")
    ) {
      handleExternalReferenceSelectChange("opposingPartyReference");
    }

    if (value === "extrajudicialLetter") {
      formContext.setValue("electronicFileFolderPath", "Außergerichtliches Verfahren");
    } else {
      formContext.setValue("electronicFileFolderPath", null);
    }
  };

  useEffect(() => {
    handleDocumentTemplateChange(formContext.getValues("documentTemplate"));
  }, []);

  const formElementConfig = getPdfGeneratorFormElementConfig({
    isMinimalView: minimalView,
    formContext,
    referenceOptions,
    handleRecipientTypeChange,
    handleExternalReferenceSelectChange,
    availableRecipients,
    recipientSelectionDisabled,
  });

  return (
    <Grid
      container
      spacing={3}
      sx={{
        alignItems: "flex-start",
      }}
    >
      {isDocumentTemplateEnabled && (
        <>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <SelectElement
              label={"Typ des Schreibens"}
              name={"documentTemplate"}
              onChange={handleDocumentTemplateChange}
            >
              {Object.entries(documentTemplates).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
            </SelectElement>
          </Grid>
          <Grid
            sx={{ display: { xs: "none", md: "block" } }}
            size={{
              md: 6,
            }}
          ></Grid>
        </>
      )}
      {formElementConfig.map(({ cols, ...config }, idx) => {
        return (
          <Grid
            key={idx + formContext.watch("documentTemplate", "") + formContext.watch("recipientType", "")}
            size={{
              xs: cols?.xs,
              md: cols?.md,
            }}
          >
            <FormElementReactHookForm {...config} product={product} />
          </Grid>
        );
      })}
      <Grid size={12}>
        <Grid
          container
          spacing={3}
          sx={{
            alignItems: "flex-start",
          }}
        >
          {!minimalView && (
            <>
              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <FormControl component="fieldset">
                  <FormLabel component="legend">Für Mandantschaft sichtbar</FormLabel>
                  <RadioButtonGroup
                    row
                    options={[
                      { id: "true", label: "Ja" },
                      { id: "false", label: "Nein", disabled: formContext.watch("sendEmailToCustomer") === "true" },
                    ]}
                    name={"showInGallery"}
                  />
                </FormControl>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <FormControl component="fieldset">
                  <FormLabel component="legend">Mandantschaft benachrichtigen</FormLabel>
                  <RadioButtonGroup
                    row
                    options={[
                      { id: "true", label: "Ja" },
                      { id: "false", label: "Nein" },
                    ]}
                    name={"sendEmailToCustomer"}
                  />
                </FormControl>
              </Grid>
            </>
          )}
          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
          >
            <SelectElement
              label={"Dokument anhängen"}
              name={"selectedMediaObjects"}
              multiple
              renderValue={renderSelectedMediaObject}
            >
              {_.map(mediaObjects, (mediaObject) => {
                const passwordProtected = isPasswordProtected(mediaObject);

                return (
                  <MenuItem
                    key={mediaObject.id}
                    value={mediaObject.id}
                    disabled={
                      mediaObject.mimeType !== "application/pdf" || mediaObject.size > 2000000 || passwordProtected
                    }
                  >
                    <ListItemIcon sx={{ justifyContent: "center" }}>
                      {passwordProtected ? (
                        <LockOutlinedIcon fontSize="small" />
                      ) : (
                        <Checkbox checked={selectedMediaObjectIds.includes(mediaObject.id)} sx={{ padding: 0 }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={mediaObject.description + " | " + dayjs(mediaObject.created).format("DD.MM.YYYY")}
                    />
                  </MenuItem>
                );
              })}
            </SelectElement>
          </Grid>
        </Grid>
      </Grid>
      <Grid sx={{ width: "100%", maxWidth: "850px", margin: "0 auto" }}>
        <Paper sx={casePdfGeneratorStyle.paper} id={"lbEditorAnchor"}>
          <LegalbirdTipTapEditor
            toolBarStickyTop={"9rem"}
            additionalToolbarActions={additionalEditorToolbarActions}
            editorSx={{ border: "none", padding: 0 }}
            sx={{ position: "relative" }}
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

const saveFormValuesBackup = _.debounce((values: UseForm["values"], product: ProductUnion) => {
  saveSessionStorageState("customPdfValues" + product.id, values);
}, 3000);

export default PdfGeneratorFormElements;
